<template>
  <div part="base" class="sp-canonical-update-modal">
    <sp-modal
      :modal-title="store.config.modalTitle"
      width="44rem"
      :open="store.isOpen"
      :closeable.prop="modalCloseable"
      :persistent.prop="store.isSubmitting"
      @update-open="handleUpdateOpen"
      @close="handleClose"
    >
      <sp-card-content-overlay slot="content-overlay" :visible="store.isSubmitting">
        <sp-progress type="ellipsis" size="x-small"></sp-progress>
      </sp-card-content-overlay>

      <div v-if="store.isLoading" class="sp-canonical-update-modal__loading">
        <sp-progress type="ellipsis" size="x-small"></sp-progress>
        <p class="sp-canonical-update-modal__loading-text">{{ store.labels.loading }}</p>
        <template v-if="store.progressPercent !== null">
          <sp-progress
            class="sp-canonical-update-modal__progress"
            type="linear"
            size="micro"
            :aria-label="store.labels.loading"
            :value.prop="store.progressPercent"
          ></sp-progress>
          <p class="sp-canonical-update-modal__loading-text">
            {{ store.progressText }}<template v-if="store.remainingText"> · {{ store.remainingText }}</template>
          </p>
        </template>
      </div>
      <CanonicalUpdateReviewStep v-else />

      <CanonicalUpdateModalActions slot="actions" />
    </sp-modal>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import CanonicalUpdateReviewStep from "./components/CanonicalUpdateReviewStep.ce.vue";
import CanonicalUpdateModalActions from "./components/CanonicalUpdateModalActions.ce.vue";
import { props as appProps } from "./props.js";
import { useCanonicalUpdateStore } from "./store/canonical-update";

const props = defineProps(appProps);

const store = useCanonicalUpdateStore();
const { exposeMethods } = useExpose();

// Only the mutating apply request locks the modal; the multi-minute preview poll
// stays dismissible. Bound with `.prop` as an explicit boolean — never undefined.
const modalCloseable = computed(() => !store.isSubmitting);

function buildConfig(overrides = {}) {
  return {
    previewUrl: props.previewUrl,
    statusUrl: props.statusUrl,
    processUrl: props.processUrl,
    modalTitle: props.modalTitle,
    statusLabels: props.statusLabels,
    labels: props.labels,
    ...overrides,
  };
}

function open(overrides) {
  store.configure(buildConfig(overrides));
  store.open();
}

function close() {
  store.close();
}

exposeMethods({ open, close });

onMounted(() => {
  store.configure(buildConfig());
});

function handleUpdateOpen({ detail }) {
  const [value] = detail;

  if (value) {
    // sp-modal re-emits update-open(true) for an open the store initiated;
    // opening again would restart the preview fetch.
    if (!store.isOpen) {
      store.open();
    }
    return;
  }

  handleClose();
}

function handleClose() {
  // Ignore dismissal only while the apply request is in flight.
  if (store.isSubmitting) {
    return;
  }

  close();
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-canonical-update-modal {
  display: block;

  --sp-comp-card-padding-block: 1rem;
  --sp-card-actions-padding-inline: 0;
  --sp-card-actions-padding-block: 0;

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sp-ref-spacing-4);
    padding-block: var(--sp-ref-spacing-12);
  }

  &__loading-text {
    font-size: var(--sp-sys-typescale-body-font-size);
    color: var(--sp-sys-color-on-surface-variant);
    margin: 0;
  }

  &__progress {
    width: min(24rem, 100%);
  }
}
</style>
